import { z } from 'zod'
import { paginatedResponseSchema, paginationQuerySchema } from './pagination'

export const reportReasonValues = ['spam', 'harassment', 'inappropriate', 'other'] as const
export type ReportReason = (typeof reportReasonValues)[number]

/** Reasons offered when flagging listing/event DATA (imported content is often
 * stale) — a superset member 'inaccurate_data' leads, user-conduct reasons stay
 * out. */
export const contentReportReasonValues = ['inaccurate_data', 'inappropriate', 'other'] as const
export type ContentReportReason = (typeof contentReportReasonValues)[number]

export const reportContentRequestSchema = z.object({
  reason: z.enum(contentReportReasonValues),
  details: z.string().max(500).optional(),
})

export const contentReportSchema = z.object({
  id: z.string().uuid(),
  entityType: z.enum(['listing', 'event']),
  entityId: z.string().uuid(),
  entityName: z.string().nullable(),
  reason: z.enum(contentReportReasonValues),
  details: z.string().nullable(),
  status: z.enum(['open', 'resolved']),
  createdAt: z.string(),
})
export type ContentReport = z.infer<typeof contentReportSchema>

export const contentReportsResponseSchema = z.object({
  reports: z.array(contentReportSchema),
})

export const reportUserRequestSchema = z.object({
  targetUserId: z.string().uuid(),
  reason: z.enum(reportReasonValues),
  details: z.string().max(500).optional(),
})

export const reportPlanRequestSchema = z.object({
  reason: z.enum(reportReasonValues),
  details: z.string().max(500).optional(),
})

/** Which report table an admin moderation action targets. */
export const moderationReportTypeValues = ['message', 'user'] as const
export type ModerationReportType = (typeof moderationReportTypeValues)[number]

export const moderationReportStatusValues = ['pending', 'resolved'] as const
export type ModerationReportStatus = (typeof moderationReportStatusValues)[number]

/** Outcome a reviewing admin records: the report was acted on, or judged unfounded. */
export const moderationActionValues = ['actioned', 'dismissed'] as const
export type ModerationAction = (typeof moderationActionValues)[number]

export const moderationMessageKindValues = [
  'text',
  'image',
  'video',
  'voice',
  'event_share',
  'listing_share',
  'plan_share',
  'plan_invite',
  'location',
  'system',
  'poll_card',
  'list_card',
  'spot_ping',
  'plan_vote',
  'drop_share',
  'drop_voucher',
  'file',
  'swipe_deck',
  'room_hi',
] as const

export const moderationConversationTypeValues = ['plan', 'direct', 'group', 'channel'] as const

/** Minimal identity of a reporter or a reported user — no contact details. */
export const moderationUserSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
})
export type ModerationUser = z.infer<typeof moderationUserSchema>

export const moderationResolutionSchema = z.object({
  action: z.enum(moderationActionValues),
  resolvedAt: z.string(),
  resolvedByAuthUserId: z.string().uuid().nullable(),
  resolvedByName: z.string().nullable(),
  note: z.string().nullable(),
})
export type ModerationResolution = z.infer<typeof moderationResolutionSchema>

/**
 * The reported message as shown to admins only. `body` is null whenever the
 * message was deleted (`deleted: true`) or carries no text — deleted content is
 * never replayed, the reviewer sees only that it is gone.
 */
export const moderationReportedMessageSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(moderationMessageKindValues),
  body: z.string().nullable(),
  deleted: z.boolean(),
  viewOnce: z.boolean(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
  sender: moderationUserSchema.nullable(),
  conversationId: z.string().uuid(),
  conversationType: z.enum(moderationConversationTypeValues),
  conversationTitle: z.string().nullable(),
})
export type ModerationReportedMessage = z.infer<typeof moderationReportedMessageSchema>

export const messageReportSchema = z.object({
  id: z.string().uuid(),
  reason: z.enum(reportReasonValues),
  details: z.string().nullable(),
  createdAt: z.string(),
  reporter: moderationUserSchema.nullable(),
  message: moderationReportedMessageSchema,
  resolution: moderationResolutionSchema.nullable(),
})
export type MessageReport = z.infer<typeof messageReportSchema>

export const userReportSchema = z.object({
  id: z.string().uuid(),
  reason: z.enum(reportReasonValues),
  details: z.string().nullable(),
  createdAt: z.string(),
  reporter: moderationUserSchema.nullable(),
  target: moderationUserSchema.nullable(),
  /** How often this target has been reported in total — repeat-offender signal. */
  targetReportCount: z.number().int().min(0),
  resolution: moderationResolutionSchema.nullable(),
})
export type UserReport = z.infer<typeof userReportSchema>

export const messageReportsResponseSchema = paginatedResponseSchema(messageReportSchema)
export const userReportsResponseSchema = paginatedResponseSchema(userReportSchema)

export const moderationQueueQuerySchema = paginationQuerySchema.extend({
  status: z.enum(moderationReportStatusValues).default('pending'),
})

export const resolveModerationReportRequestSchema = z.object({
  action: z.enum(moderationActionValues),
  note: z.string().max(500).optional(),
})

export const resolveModerationReportResponseSchema = z.object({
  id: z.string().uuid(),
  resolution: moderationResolutionSchema,
})
